import { goRequest } from '../utils/httpClientTs';
import {
  ReciprocityListResponse,
  ReciprocityDetail,
  ReciprocityUpdate,
  ReciprocityMemberAdd,
  ReciprocityContactUpdate,
  ReciprocityMemberReplace,
  ReciprocityChapterAdd,
  ReciprocityMessage,
  ReciprocityRole,
  ReciprocitySPDChapter,
  ReciprocityCreate,
} from '../types/reciprocity';

export interface ReciprocityListParams {
  rowsPerPage: number;
  page: number;
  orderBy?: string;
  order?: string;
  search?: string;
  reciprocityType?: string;
}

export const getReciprocityList = async (
  params: ReciprocityListParams
): Promise<ReciprocityListResponse> => {
  let url = `/api/resources/reciprocity?page_size=${params.rowsPerPage}&page=${params.page}`;

  if (params.search) {
    url += `&search=${encodeURIComponent(params.search)}`;
  }
  if (params.orderBy) {
    url += `&order_by=${params.orderBy}`;
  }
  if (params.order) {
    url += `&order=${params.order}`;
  }
  if (params.reciprocityType && params.reciprocityType !== 'all') {
    url += `&reciprocity_type=${encodeURIComponent(params.reciprocityType)}`;
  }

  return goRequest<ReciprocityListResponse>({
    url: url,
    method: 'GET',
  });
};

export const getReciprocityDetail = async (
  id: string
): Promise<ReciprocityDetail> => {
  return goRequest<ReciprocityDetail>({
    url: `/api/resources/reciprocity/${id}`,
    method: 'GET',
  });
};

export const updateReciprocity = async (
  id: string,
  data: ReciprocityUpdate
): Promise<any> => {
  return goRequest({
    url: `/api/resources/reciprocity/${id}`,
    method: 'PATCH',
    data,
  });
};

export const addReciprocityMember = async (
  id: string,
  data: ReciprocityMemberAdd
): Promise<any> => {
  return goRequest({
    url: `/api/resources/reciprocity/${id}/members`,
    method: 'POST',
    data,
  });
};

export const updateReciprocityContact = async (
  id: string,
  memberId: string,
  data: ReciprocityContactUpdate
): Promise<any> => {
  return goRequest({
    url: `/api/resources/reciprocity/${id}/members/${memberId}`,
    method: 'PATCH',
    data,
  });
};

export const deleteReciprocityContact = async (
  id: string,
  memberId: string
): Promise<any> => {
  return goRequest({
    url: `/api/resources/reciprocity/${id}/members/${memberId}`,
    method: 'DELETE',
  });
};

export const replaceReciprocityMember = async (
  id: string,
  memberId: string,
  data: ReciprocityMemberReplace
): Promise<any> => {
  return goRequest({
    url: `/api/resources/reciprocity/${id}/members/${memberId}/replace`,
    method: 'POST',
    data,
  });
};

export const addReciprocityChapter = async (
  id: string,
  data: ReciprocityChapterAdd
): Promise<any> => {
  return goRequest({
    url: `/api/resources/reciprocity/${id}/chapters`,
    method: 'POST',
    data,
  });
};

export const removeReciprocityChapter = async (
  id: string,
  chapterId: string
): Promise<any> => {
  return goRequest({
    url: `/api/resources/reciprocity/${id}/chapters/${chapterId}`,
    method: 'DELETE',
  });
};

export const sendReciprocityMessage = async (
  id: string,
  data: ReciprocityMessage
): Promise<any> => {
  return goRequest({
    url: `/api/resources/reciprocity/${id}/message`,
    method: 'POST',
    data,
  });
};

export const getReciprocityRoles = async (): Promise<ReciprocityRole[]> => {
  return goRequest<ReciprocityRole[]>({
    url: '/api/resources/reciprocity/roles',
    method: 'GET',
  });
};

export const getReciprocitySPDChapter = async (
  id: string
): Promise<ReciprocitySPDChapter[]> => {
  return goRequest<ReciprocitySPDChapter[]>({
    url: `/api/resources/reciprocity/spd_chapter/${id}`,
    method: 'GET',
  });
};

export const createReciprocity = async (
  data: ReciprocityCreate
): Promise<any> => {
  return goRequest({
    url: '/api/resources/reciprocity/create',
    method: 'POST',
    data,
  });
};

/** Scoped permissions for the list page (create button + financial fields for this SPD). */
export const getReciprocityPermissions = async (
  spdChapterId: string
): Promise<{ can_manage: boolean; can_manage_financial: boolean }> => {
  const params = new URLSearchParams({ spd_chapter_id: spdChapterId });
  return goRequest<{
    can_manage: boolean;
    can_manage_financial: boolean;
  }>({
    url: `/api/resources/reciprocity/permissions?${params.toString()}`,
    method: 'GET',
  });
};

export const checkFinancialPermission = async (): Promise<{
  can_edit_financial: boolean;
}> => {
  return goRequest<{ can_edit_financial: boolean }>({
    url: '/api/resources/reciprocity/permissions/financial',
    method: 'GET',
  });
};
